// Full-document render of the Oscar pay-later completion view: the checkout
// header + flash messages in the PageSkeleton header slot, the CyberSource
// fingerprint + Google Maps loader scripts, and the
// `#complete-deferred-payment-app` SPA mount fed the view's init_data payload
// (the checkout island reads it from the data-init-data attribute).
import React from "react";

import { templates } from "@reactivated";

import { DeferredPaymentApp } from "../apps/checkout/DeferredPaymentApp";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CheckoutHeader } from "./CheckoutHeaderTemplate";
import { Template as MessageBanner } from "./MessageBannerTemplate";

export const Template = (
    props: templates.CompleteDeferredPaymentDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            /* The header slot renders the flash queue as MessageBanners (plus a
               trailing blank one that surfaces the Redux payment_error) and the
               checkout header, so the default messages block is suppressed. */
            messages={false}
            header={
                <>
                    {props.page_chrome.messages.map((message, i) => (
                        <MessageBanner
                            key={i}
                            tags={message.tags}
                            message={message.message}
                        />
                    ))}
                    <MessageBanner tags="" message="" />
                    <CheckoutHeader {...props.checkout_header} />
                </>
            }
        >
            {/* React 19 hoists this <meta> into <head>; the description meta
                renders from page_chrome.search_description. */}
            <meta name="chatTags" content="checkout" />
            {/* CyberSource device fingerprint (fraud-critical). Keep this a
                classic parser-blocking script so profiling completes before
                the payment UI becomes available. */}
            <p
                style={{
                    background: `url(${props.fingerprint_img_1_url})`,
                }}
            />
            <img src={props.fingerprint_img_2_url} alt="" />
            <object
                type="application/x-shockwave-flash"
                data={props.fingerprint_flash_url}
                width={1}
                height={1}
                id="thm_fp"
            >
                <param name="movie" value={props.fingerprint_flash_url} />
                <div />
            </object>
            <script src={props.fingerprint_js_url} type="text/javascript" />
            {/* Google Maps Places powers the address-autocomplete inputs. async
                so React 19 hoists it to <head> (matched by src) rather than a
                positional body <script> that trips a full-document hydration
                mismatch; it still loads well before the address step renders. */}
            <script
                src={`https://maps.googleapis.com/maps/api/js?v=3&key=${props.google_maps_api_key}&libraries=places`}
                type="text/javascript"
                async
            />
            {/* The complete-deferred-payment app lives in the document's React
                tree (mounted client-side by DeferredPaymentApp; the dependency
                tree is not SSR-safe). Its init_data payload is threaded in as a
                prop (from the reactivated document prop, carried to the client
                via __PRELOADED_PROPS__) rather than a data-init-data attribute
                the old imperative island read off the DOM. */}
            <div className="l-capped-width">
                <div
                    id="complete-deferred-payment-app"
                    className="checkout-app-container"
                >
                    <DeferredPaymentApp initData={props.init_data} />
                </div>
            </div>
        </PageSkeleton>
    );
};
